import { Component, OnDestroy, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { Dialog } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { Checkbox } from 'primeng/checkbox';
import { Tag } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

import { ContractMaintenance, ContractAuditLogEntry, ContractPriceReview, ContractJob } from '../../models/contract-maintenance.model';
import { ContractMaintenanceService } from '../../services/contract-maintenance.service';
import { Client } from '../../models/client.model';
import { ClientService } from '../../services/client.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { CountyService } from '../../services/county.service';
import { County } from '../../models/county.model';
import { CompanyService } from '../../services/company.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

/** One MSA with its linked SOW/Proposal/Schedule (expandable row under client). */
export interface MsaWithChildren {
  msa: ContractMaintenance;
  expanded: boolean;
  children: ContractMaintenance[];
}

export interface CustomerWithContracts {
  clientId: string;
  clientName: string;
  folderNumber?: string | null;
  contracts: ContractMaintenance[];
  /** MSA-type contracts for this client. */
  msaContracts: ContractMaintenance[];
  /** SOW / Proposal / Schedule contracts for this client. */
  childContracts: ContractMaintenance[];
  /** MSAs with their linked children (for Client → MSA → linked SOW/Proposal/Schedule). */
  msaWithChildren: MsaWithChildren[];
  /** Child contracts not linked to any MSA. */
  standaloneChildren: ContractMaintenance[];
  expanded: boolean;
}

/**
 * One job as edited in the Add/Edit Job dialog. Dates are Date objects for p-datepicker and
 * are converted back to strings on save. `id` is null for a job being added, which is what
 * distinguishes a POST from a PUT.
 */
export interface ContractJobDraft {
  id: string | null;
  name: string;
  price: number | null;
  lastPriceReviewed: Date | null;
  nextPriceRenewalDate: Date | null;
  comments: string;
}

export type ContractAuditFieldType = 'text' | 'date' | 'bool' | 'number';

/** One field's Old -> New transition within a single history version. */
export interface ContractHistoryChange {
  key: string;
  label: string;
  previous: string;
  current: string;
}

/**
 * Every field captured in the contract audit snapshot, with its display label and
 * value type. Mirrors AuditedFields in ContractMaintenanceService on the backend —
 * keep the two in sync when adding a contract field.
 */
export const CONTRACT_AUDIT_FIELDS: { key: string; label: string; type: ContractAuditFieldType }[] = [
  // Identity / linkage
  { key: 'ClientName', label: 'Customer', type: 'text' },
  { key: 'FolderNumber', label: 'Folder #', type: 'text' },
  { key: 'ContractType', label: 'Contract Type', type: 'text' },
  { key: 'MsaName', label: 'MSA Name', type: 'text' },
  { key: 'ParentMsaContractId', label: 'Linked MSA', type: 'text' },
  { key: 'ProjectName', label: 'Project Name', type: 'text' },
  { key: 'Status', label: 'Status', type: 'text' },
  { key: 'ServiceGroupName', label: 'Service Group', type: 'text' },
  { key: 'CountryName', label: 'Country', type: 'text' },
  { key: 'CompanyName', label: 'Company', type: 'text' },
  { key: 'DisableMsaNotifications', label: 'MSA Notifications Disabled', type: 'bool' },
  { key: 'EffectiveDate', label: 'Effective Date', type: 'date' },

  // MSA
  { key: 'MsaSignedDate', label: 'MSA Signed Date', type: 'date' },
  { key: 'MsaExpiryDate', label: 'MSA Expiry Date', type: 'date' },
  { key: 'MsaDuration', label: 'MSA Duration', type: 'text' },

  // SOW
  { key: 'SowSignedDate', label: 'SOW Signed Date', type: 'date' },
  { key: 'SowExpiryDate', label: 'SOW Expiry Date', type: 'date' },
  { key: 'SowDuration', label: 'SOW Duration', type: 'text' },

  // Schedule
  { key: 'ScheduleSignedDate', label: 'Schedule Signed Date', type: 'date' },
  { key: 'ScheduleExpiryDate', label: 'Schedule Expiry Date', type: 'date' },
  { key: 'ScheduleDuration', label: 'Schedule Duration', type: 'text' },

  // Proposal
  { key: 'ProposalSignedDate', label: 'Proposal Signed Date', type: 'date' },
  { key: 'ProposalExpiryDate', label: 'Proposal Expiry Date', type: 'date' },
  { key: 'ProposalDuration', label: 'Proposal Duration', type: 'text' },

  // Commercial / contract details
  { key: 'LastPriceReviewed', label: 'Last Price Reviewed', type: 'date' },
  { key: 'NextPriceRenewalDate', label: 'Next Price Renewal Date', type: 'date' },
  { key: 'PriceReviewRemarks', label: 'Price Review Remarks', type: 'text' },
  { key: 'TerminationNoticePeriod', label: 'Termination Notice Period', type: 'text' },
  { key: 'Price', label: 'Price', type: 'number' },
  { key: 'RateBase', label: 'Rate Base', type: 'text' },
  { key: 'BtgServiceCharge', label: 'BTG Service Charge', type: 'number' },
  { key: 'BtgServiceFee', label: 'BTG Service Fee', type: 'number' },
  { key: 'CpiServiceCharge', label: 'CPI Service Charge', type: 'number' },
  { key: 'CpiClause', label: 'CPI Clause', type: 'text' },
  { key: 'ContractLessThanOneYear', label: 'Contract < 1 Year', type: 'bool' },
  { key: 'ContractForOneYear', label: 'Contract = 1 Year', type: 'bool' },
  { key: 'ContractGreaterThanOneYear', label: 'Contract > 1 Year', type: 'bool' },

  // Free text
  { key: 'Note', label: 'Note', type: 'text' }
];

@Component({
  selector: 'app-contracts-maintenance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    InputText,
    DatePicker,
    Select,
    Dialog,
    Textarea,
    Checkbox,
    Tag,
    TooltipModule,
    ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './contracts-maintenance.component.html',
  styleUrl: './contracts-maintenance.component.scss'
})
export class ContractsMaintenanceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  loading = false;

  contracts: ContractMaintenance[] = [];
  filteredContracts: ContractMaintenance[] = [];
  customerGroups: CustomerWithContracts[] = [];
  filteredCustomerGroups: CustomerWithContracts[] = [];

  rows = 20;
  rowsPerPageOptions = [10, 20, 50];
  first = 0;
  totalRecords = 0;

  searchText = '';
  selectedStatus: string | null = null;
  selectedCustomerId: string | null = null;
  durationFilterValue: number | null = null;
  durationFilterUnit: 'Month' | 'Year' | null = null;

  statusOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
  ];

  statusOptionsWithoutAll = this.statusOptions.filter(o => o.value !== null);

  durationUnitOptions = [
    { label: 'Month(s)', value: 'Month' },
    { label: 'Year(s)', value: 'Year' }
  ];

  terminationNoticePeriodUnitOptions = [
    { label: 'Days', value: 'Days' },
    { label: 'Weeks', value: 'Weeks' },
    { label: 'Months', value: 'Months' }
  ];

  contractTypeOptions = [
    { label: 'MSA', value: 'MSA' },
    { label: 'SOW', value: 'SOW' },
    { label: 'Proposal', value: 'Proposal' },
    { label: 'Schedule', value: 'Schedule' }
  ];

  /** MSAs for the selected client (used when contract type is SOW/Proposal/Schedule). */
  msaContractsForClient: ContractMaintenance[] = [];

  fieldTouched: Record<string, boolean> = {};

  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  selectedContract: ContractMaintenance | null = null;

  // Contract Details Dialog
  displayContractDetailsDialog = false;
  selectedContractForDetails: ContractMaintenance | null = null;
  currentYear = new Date().getFullYear();

  // Contract History Dialog
  displayHistoryDialog = false;
  historyContract: ContractMaintenance | null = null;
  contractHistoryEntries: ContractAuditLogEntry[] = [];
  loadingHistory = false;
  /** Audit-log entry id -> whether its field-level change detail is expanded. */
  expandedHistoryVersions: Record<string, boolean> = {};
  readonly contractAuditFields = CONTRACT_AUDIT_FIELDS;

  // Price Review history (Contract Details dialog)
  priceReviews: ContractPriceReview[] = [];
  loadingPriceReviews = false;
  priceReviewsExpanded = true;
  /** Review opened in the read-only detail popup. */
  selectedPriceReview: ContractPriceReview | null = null;
  displayPriceReviewDialog = false;

  // Jobs dialog — priced jobs under a contract, listed read-only
  displayJobsDialog = false;
  jobsContract: ContractMaintenance | null = null;
  jobs: ContractJob[] = [];
  loadingJobs = false;

  // Add / Edit Job dialog. Each job saves on its own so an untouched job is never re-sent,
  // which would otherwise put a no-op row in the audit log — the only change record jobs have.
  displayJobFormDialog = false;
  jobFormMode: 'add' | 'edit' = 'add';
  jobForm: ContractJobDraft | null = null;
  savingJob = false;

  // Contract Details Form Fields
  contractDetails = {
    lastPriceReviewed: null as Date | null,
    nextPriceRenewalDate: null as Date | null,
    priceReviewRemarks: '',
    terminationNoticePeriodValue: null as number | null,
    terminationNoticePeriodUnit: 'Days' as 'Days' | 'Weeks' | 'Months',
    price: null as number | null,
    rateBase: '',
    btgServiceCharge: null as number | null,
    btgServiceFee: null as number | null,
    cpiServiceCharge: null as number | null,
    cpiClause: '',
    contractLessThanOneYear: false,
    contractForOneYear: false,
    contractGreaterThanOneYear: false
  };

  clients: Client[] = [];
  serviceGroups: { id: string; name: string }[] = [];
  countries: { id: string; name: string }[] = [];
  companies: { id: string; name: string }[] = [];

  constructor(
    private contractService: ContractMaintenanceService,
    private clientService: ClientService,
    private serviceGroupService: ServiceGroupService,
    private countyService: CountyService,
    private companyService: CompanyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) { }

  ngOnInit(): void {
    // Header integration: search + Export + primary action (Add Contract)
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search contracts...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setCanShowPrimaryAction(true);
    this.pageHeaderService.setPrimaryButtonLabel('Add Contract');
    this.pageHeaderService.setExportDisabled(true);
    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.searchText = text ?? '';
        this.applyFilters();
      });
    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());
    this.pageHeaderService.primaryActionClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showAddDialog());

    this.loadLookups();
    this.loadContracts();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private loadLookups(): void {
    this.clientService.getAllClients().subscribe({
      next: list => {
        this.clients = (list || []).filter(c => c.isActive === true);
      },
      error: () => (this.clients = [])
    });
    this.serviceGroupService.getAllServiceGroups().subscribe({
      next: list => {
        this.serviceGroups = (list || []).map(sg => ({ id: sg.id, name: sg.name }));
      },
      error: () => (this.serviceGroups = [])
    });
    this.countyService.getAllCounties().subscribe({
      next: (list: County[]) => {
        this.countries = (list || [])
          .filter((ct: County) => ct.isActive === true)
          .map((ct: County) => ({ id: ct.id!, name: ct.name }));
      },
      error: () => (this.countries = [])
    });
    this.companyService.getAllCompanies().subscribe({
      next: list => {
        this.companies = (list || [])
          .filter(c => c.isActive === true && c.id != null && c.name != null)
          .map(c => ({ id: c.id!, name: c.name }));
      },
      error: () => (this.companies = [])
    });
  }

  private loadContracts(): void {
    this.loading = true;
    this.contractService.getAll().subscribe({
      next: list => {
        this.contracts = list || [];
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.contracts = [];
        this.filteredContracts = [];
        this.customerGroups = [];
        this.filteredCustomerGroups = [];
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load contracts'
        });
      }
    });
  }

  onSearch(): void { this.applyFilters(); }
  onStatusFilterChange(): void { this.applyFilters(); }
  onCustomerFilterChange(): void { this.applyFilters(); }
  onDurationFilterChange(): void { this.applyFilters(); }

  /** Drives the clear-filters button's visibility, as on every other list page. */
  hasActiveFilters(): boolean {
    return !!(
      this.searchText?.trim() ||
      this.selectedStatus ||
      this.selectedCustomerId ||
      this.durationFilterValue ||
      this.durationFilterUnit
    );
  }

  clearFilters(): void {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatus = null;
    this.selectedCustomerId = null;
    this.durationFilterValue = null;
    this.durationFilterUnit = null;
    this.applyFilters();
  }

  private applyFilters(): void {
    let list = this.contracts;
    const term = this.searchText.trim().toLowerCase();
    if (term) {
      list = list.filter(c =>
        (c.clientName ?? '').toLowerCase().includes(term) ||
        (c.projectName ?? '').toLowerCase().includes(term) ||
        (c.msaName ?? '').toLowerCase().includes(term) ||
        (c.serviceGroupName ?? '').toLowerCase().includes(term) ||
        (c.countryName ?? '').toLowerCase().includes(term) ||
        (c.folderNumber ?? '').toLowerCase().includes(term) ||
        (c.companyName ?? '').toLowerCase().includes(term)
      );
    }
    if (this.selectedStatus) {
      list = list.filter(c => c.status === this.selectedStatus);
    }
    if (this.selectedCustomerId) {
      list = list.filter(c => c.clientId === this.selectedCustomerId);
    }
    if (this.durationFilterValue != null && this.durationFilterUnit != null) {
      const val = this.durationFilterValue;
      const unit = this.durationFilterUnit;
      list = list.filter(c => this.contractMatchesDuration(c, val, unit));
    }
    this.filteredContracts = list;
    this.totalRecords = list.length;
    this.buildCustomerGroups();
    this.pageHeaderService.setExportDisabled(this.filteredContracts.length === 0);
  }

  /** Match contract by duration value and unit, using either Value/Unit fields or parsed duration string from API. */
  private contractMatchesDuration(c: ContractMaintenance, val: number, unit: 'Month' | 'Year'): boolean {
    const match = (v: number | null | undefined, u: 'Month' | 'Year' | undefined) =>
      v === val && u === unit;
    if (match(c.msaDurationValue, c.msaDurationUnit) || match(c.sowDurationValue, c.sowDurationUnit) ||
        match(c.scheduleDurationValue, c.scheduleDurationUnit) || match(c.proposalDurationValue, c.proposalDurationUnit)) {
      return true;
    }
    const parsedMsa = this.parseDurationString(c.msaDuration);
    const parsedSow = this.parseDurationString(c.sowDuration);
    const parsedSchedule = this.parseDurationString(c.scheduleDuration);
    const parsedProposal = this.parseDurationString(c.proposalDuration);
    return (
      (parsedMsa != null && parsedMsa.value === val && parsedMsa.unit === unit) ||
      (parsedSow != null && parsedSow.value === val && parsedSow.unit === unit) ||
      (parsedSchedule != null && parsedSchedule.value === val && parsedSchedule.unit === unit) ||
      (parsedProposal != null && parsedProposal.value === val && parsedProposal.unit === unit)
    );
  }

  /** Parse "5 months" / "1 year" style string into value and unit. */
  private parseDurationString(duration: string | null | undefined): { value: number; unit: 'Month' | 'Year' } | null {
    if (duration == null || typeof duration !== 'string') return null;
    const raw = duration.trim();
    const match = raw.match(/^(\d+)\s*(month|year)s?$/i);
    if (!match) return null;
    const value = +match[1];
    const unit = match[2].toLowerCase() === 'year' ? 'Year' as const : 'Month' as const;
    return { value, unit };
  }

  private buildCustomerGroups(): void {
    // The groups are rebuilt on every filter change and after every save. Remember which
    // rows were open first, so saving a contract does not collapse the tree and lose the
    // user's place.
    const expandedCustomers = new Set(
      this.customerGroups.filter(g => g.expanded).map(g => g.clientId || g.clientName || 'unknown')
    );
    const expandedMsas = new Set(
      this.customerGroups.flatMap(g => g.msaWithChildren.filter(m => m.expanded).map(m => m.msa.id))
    );

    const map = new Map<string, CustomerWithContracts>();
    for (const c of this.filteredContracts) {
      const key = c.clientId || c.clientName || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          clientId: c.clientId,
          clientName: c.clientName || 'Unknown',
          folderNumber: c.folderNumber,
          contracts: [],
          msaContracts: [],
          childContracts: [],
          msaWithChildren: [],
          standaloneChildren: [],
          expanded: expandedCustomers.has(key)
        });
      }
      const group = map.get(key)!;
      group.contracts.push(c);
      if (c.contractType === 'MSA') {
        group.msaContracts.push(c);
      } else if (c.contractType === 'SOW' || c.contractType === 'Proposal' || c.contractType === 'Schedule') {
        group.childContracts.push(c);
      }
      // Legacy contracts (null contractType) stay in contracts only; could push to childContracts for display
      if (!c.contractType && (c.sowSignedDate || c.sowDuration || c.sowExpiryDate || c.proposalSignedDate || c.proposalDuration || c.proposalExpiryDate)) {
        group.childContracts.push(c);
      }
    }
    // Build msaWithChildren (MSA → linked SOW/Proposal/Schedule) and standaloneChildren per customer
    for (const group of map.values()) {
      group.msaWithChildren = group.msaContracts.map(msa => ({
        msa,
        expanded: expandedMsas.has(msa.id),
        children: group.childContracts.filter(ch => ch.parentMsaContractId === msa.id)
      }));
      group.standaloneChildren = group.childContracts.filter(ch => !ch.parentMsaContractId);
    }
    this.customerGroups = Array.from(map.values()).sort((a, b) =>
      (a.clientName || '').localeCompare(b.clientName || '')
    );
    this.filteredCustomerGroups = this.customerGroups;
  }

  /** Toggle expand/collapse of an MSA row (show linked SOW/Proposal/Schedule). */
  toggleMsa(item: MsaWithChildren): void {
    item.expanded = !item.expanded;
  }

  /** Get display name for the linked MSA (for SOW/Proposal/Schedule rows). */
  getLinkedMsaName(row: ContractMaintenance, customer: CustomerWithContracts): string {
    if (!row.parentMsaContractId) return '—';
    const msa = customer.msaContracts.find(m => m.id === row.parentMsaContractId);
    return msa?.msaName ?? row.parentMsaName ?? '—';
  }

  /** Get document signed date for a child contract (SOW/Proposal/Schedule). */
  getDocumentSignedDate(row: ContractMaintenance): string | null {
    if (row.contractType === 'SOW') return row.sowSignedDate ?? null;
    if (row.contractType === 'Schedule') return row.scheduleSignedDate ?? null;
    if (row.contractType === 'Proposal') return row.proposalSignedDate ?? null;
    return row.sowSignedDate ?? row.proposalSignedDate ?? null;
  }

  /** Get document duration for a child contract. */
  getDocumentDuration(row: ContractMaintenance): string {
    if (row.contractType === 'SOW') return row.sowDuration ?? '—';
    if (row.contractType === 'Schedule') return row.scheduleDuration ?? '—';
    if (row.contractType === 'Proposal') return row.proposalDuration ?? '—';
    return row.sowDuration ?? row.proposalDuration ?? '—';
  }

  /** Get document expiry date for a child contract. */
  getDocumentExpiryDate(row: ContractMaintenance): string | null {
    if (row.contractType === 'SOW') return row.sowExpiryDate ?? null;
    if (row.contractType === 'Schedule') return row.scheduleExpiryDate ?? null;
    if (row.contractType === 'Proposal') return row.proposalExpiryDate ?? null;
    return row.sowExpiryDate ?? row.proposalExpiryDate ?? null;
  }

  toggleCustomer(customer: CustomerWithContracts): void {
    customer.expanded = !customer.expanded;
  }

  expandAll(): void {
    this.filteredCustomerGroups.forEach(c => (c.expanded = true));
  }

  collapseAll(): void {
    this.filteredCustomerGroups.forEach(c => (c.expanded = false));
  }

  showAddDialog(): void {
    this.dialogMode = 'add';
    this.selectedContract = {
      id: '',
      clientId: '',
      clientName: '',
      projectId: undefined,
      projectName: '',
      serviceGroupId: '',
      serviceGroupName: '',
      countryId: '',
      countryName: '',
      companyId: null,
      companyName: null,
      folderNumber: null,
      contractType: 'MSA',
      msaName: null,
      parentMsaContractId: null,
      parentMsaName: null,
      status: 'Active',
      msaDurationValue: null,
      msaDurationUnit: 'Month',
      sowDurationValue: null,
      sowDurationUnit: 'Month',
      scheduleDurationValue: null,
      scheduleDurationUnit: 'Month',
      proposalDurationValue: null,
      proposalDurationUnit: 'Month',
      disableMsaNotifications: false,
      effectiveDate: null,
      note: null
    };
    this.msaContractsForClient = [];
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  showEditDialog(contract: ContractMaintenance): void {
    this.dialogMode = 'edit';
    this.selectedContract = { ...contract };
    this.parseDurationToValueUnit('msa');
    this.parseDurationToValueUnit('sow');
    this.parseDurationToValueUnit('schedule');
    this.parseDurationToValueUnit('proposal');
    if (this.selectedContract) {
      (this.selectedContract as any).effectiveDate = this.parseToDate(this.selectedContract.effectiveDate as string);
      (this.selectedContract as any).msaSignedDate = this.parseToDate(this.selectedContract.msaSignedDate as string);
      (this.selectedContract as any).msaExpiryDate = this.parseToDate(this.selectedContract.msaExpiryDate as string);
      (this.selectedContract as any).sowSignedDate = this.parseToDate(this.selectedContract.sowSignedDate as string);
      (this.selectedContract as any).sowExpiryDate = this.parseToDate(this.selectedContract.sowExpiryDate as string);
      (this.selectedContract as any).scheduleSignedDate = this.parseToDate(this.selectedContract.scheduleSignedDate as string);
      (this.selectedContract as any).scheduleExpiryDate = this.parseToDate(this.selectedContract.scheduleExpiryDate as string);
      (this.selectedContract as any).proposalSignedDate = this.parseToDate(this.selectedContract.proposalSignedDate as string);
      (this.selectedContract as any).proposalExpiryDate = this.parseToDate(this.selectedContract.proposalExpiryDate as string);
    }
    // Load MSAs for this client for the dropdown when editing SOW/Proposal/Schedule
    this.refreshMsaContractsForClient();
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  private parseToDate(value: string | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private toDateString(value: Date | string | null | undefined): string | null {
    if (value == null) return null;
    if (value instanceof Date) {
      // Return ISO date string format (yyyy-MM-dd) using local date to avoid timezone issues
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return typeof value === 'string' ? value : null;
  }

  private parseDurationToValueUnit(prefix: 'msa' | 'sow' | 'schedule' | 'proposal'): void {
    if (!this.selectedContract) return;
    const key = prefix + 'Duration' as keyof ContractMaintenance;
    const raw = (this.selectedContract[key] as string) || '';
    const match = raw.match(/^(\d+)\s*(month|year)s?$/i);
    const valKey = (prefix + 'DurationValue') as keyof ContractMaintenance;
    const unitKey = (prefix + 'DurationUnit') as keyof ContractMaintenance;
    if (match) {
      (this.selectedContract as any)[valKey] = +match[1];
      (this.selectedContract as any)[unitKey] = match[2].toLowerCase() === 'year' ? 'Year' : 'Month';
    } else {
      (this.selectedContract as any)[valKey] = null;
      (this.selectedContract as any)[unitKey] = 'Month';
    }
  }

  private buildDurationString(value: number | null | undefined, unit: string | undefined): string | null {
    if (value == null || value === undefined) return null;
    const u = unit === 'Year' ? 'year' : 'month';
    const s = value !== 1 ? 's' : '';
    return `${value} ${u}${s}`;
  }

  onClientChange(): void {
    if (!this.selectedContract) return;
    const client = this.clients.find(c => c.id === this.selectedContract!.clientId);
    if (client) {
      this.selectedContract.clientName = client.name;
      this.selectedContract.folderNumber = client.folderNumber ?? null;
    } else {
      this.selectedContract.clientName = '';
      this.selectedContract.folderNumber = null;
    }
    this.refreshMsaContractsForClient();
  }

  onCompanyChange(): void {
    if (!this.selectedContract) return;
    const company = this.companies.find(c => c.id === this.selectedContract!.companyId);
    this.selectedContract.companyName = company ? company.name : null;
  }

  onServiceGroupChange(): void {
    if (!this.selectedContract) return;
    const sg = this.serviceGroups.find(s => s.id === this.selectedContract!.serviceGroupId);
    this.selectedContract.serviceGroupName = sg ? sg.name : '';
  }

  onCountryChange(): void {
    if (!this.selectedContract) return;
    const country = this.countries.find(c => c.id === this.selectedContract!.countryId);
    this.selectedContract.countryName = country ? country.name : '';
  }

  onContractTypeChange(): void {
    if (!this.selectedContract) return;
    // When switching type, clear document-specific fields to avoid cross-contamination
    if (this.selectedContract.contractType === 'MSA') {
      this.selectedContract.projectName = '';
      this.selectedContract.parentMsaContractId = null;
      this.selectedContract.sowSignedDate = null;
      this.selectedContract.sowDuration = null;
      this.selectedContract.sowDurationValue = null;
      this.selectedContract.sowDurationUnit = 'Month';
      this.selectedContract.sowExpiryDate = null;
      this.selectedContract.scheduleSignedDate = null;
      this.selectedContract.scheduleDuration = null;
      this.selectedContract.scheduleDurationValue = null;
      this.selectedContract.scheduleDurationUnit = 'Month';
      this.selectedContract.scheduleExpiryDate = null;
      this.selectedContract.proposalSignedDate = null;
      this.selectedContract.proposalDuration = null;
      this.selectedContract.proposalDurationValue = null;
      this.selectedContract.proposalDurationUnit = 'Month';
      this.selectedContract.proposalExpiryDate = null;
    } else {
      // For SOW/Proposal/Schedule we keep projectName and MSA link;
      // caller ensures appropriate duration/date fields are mapped on save.
    }
  }

  private refreshMsaContractsForClient(): void {
    if (!this.selectedContract || !this.selectedContract.clientId) {
      this.msaContractsForClient = [];
      return;
    }
    const clientId = this.selectedContract.clientId;
    this.msaContractsForClient = this.contracts.filter(c =>
      c.clientId === clientId && c.contractType === 'MSA'
    );
    if (this.msaContractsForClient.length === 1 &&
        this.selectedContract.contractType !== 'MSA' &&
        !this.selectedContract.parentMsaContractId) {
      this.selectedContract.parentMsaContractId = this.msaContractsForClient[0].id;
    }
  }

  hideDialog(): void {
    this.displayDialog = false;
    this.selectedContract = null;
    this.fieldTouched = {};
  }

  isContractFormValid(): boolean {
    if (!this.selectedContract) return false;
    return !!(
      this.selectedContract.clientId &&
      this.selectedContract.serviceGroupId &&
      this.selectedContract.countryId &&
      this.selectedContract.status &&
      this.selectedContract.contractType
    );
  }

  saveContract(): void {
    if (!this.selectedContract) return;

    // Mark all required fields as touched
    this.fieldTouched['clientId'] = true;
    this.fieldTouched['serviceGroupId'] = true;
    this.fieldTouched['countryId'] = true;
    this.fieldTouched['status'] = true;
    this.fieldTouched['contractType'] = true;

    if (!this.isContractFormValid()) {
      return;
    }

    const c = this.selectedContract;
    const dto: ContractMaintenance = {
      ...c,
      projectId: undefined,
      projectName: c.projectName || '',
      companyId: c.companyId || null,
      companyName: c.companyName || null,
      effectiveDate: this.toDateString(c.effectiveDate),
      msaSignedDate: this.toDateString(c.msaSignedDate),
      msaExpiryDate: this.toDateString(c.msaExpiryDate),
      sowSignedDate: this.toDateString(c.sowSignedDate),
      sowExpiryDate: this.toDateString(c.sowExpiryDate),
      scheduleSignedDate: this.toDateString(c.scheduleSignedDate),
      scheduleExpiryDate: this.toDateString(c.scheduleExpiryDate),
      proposalSignedDate: this.toDateString(c.proposalSignedDate),
      proposalExpiryDate: this.toDateString(c.proposalExpiryDate),
      msaDuration: this.buildDurationString(c.msaDurationValue, c.msaDurationUnit),
      sowDuration: this.buildDurationString(c.sowDurationValue, c.sowDurationUnit),
      scheduleDuration: this.buildDurationString(c.scheduleDurationValue, c.scheduleDurationUnit),
      proposalDuration: this.buildDurationString(c.proposalDurationValue, c.proposalDurationUnit)
    };

    if (this.dialogMode === 'add') {
      this.contractService.create(dto).subscribe({
        next: (created) => {
          this.contracts.push(created);
          this.applyFilters();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Contract created successfully' });
          this.displayDialog = false;
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create contract' });
        }
      });
    } else {
      this.contractService.update(this.selectedContract.id, dto).subscribe({
        next: (updated) => {
          const idx = this.contracts.findIndex(c => c.id === updated.id);
          if (idx >= 0) this.contracts[idx] = updated;
          this.applyFilters();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Contract updated successfully' });
          this.displayDialog = false;
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update contract' });
        }
      });
    }
  }

  showContractDetails(contract: ContractMaintenance): void {
    this.selectedContractForDetails = { ...contract };

    // Parse termination notice period if it exists (format: "30 Days", "2 Weeks", etc.)
    let terminationValue: number | null = null;
    let terminationUnit: 'Days' | 'Weeks' | 'Months' = 'Days';

    if (contract.terminationNoticePeriod) {
      const match = contract.terminationNoticePeriod.match(/^(\d+)\s*(Days?|Weeks?|Months?)$/i);
      if (match) {
        terminationValue = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();
        if (unit.startsWith('week')) {
          terminationUnit = 'Weeks';
        } else if (unit.startsWith('month')) {
          terminationUnit = 'Months';
        } else {
          terminationUnit = 'Days';
        }
      }
    }

    // Load existing contract details if available
    this.contractDetails = {
      lastPriceReviewed: contract.lastPriceReviewed ? this.parseToDate(contract.lastPriceReviewed) : null,
      nextPriceRenewalDate: contract.nextPriceRenewalDate ? this.parseToDate(contract.nextPriceRenewalDate) : null,
      priceReviewRemarks: contract.priceReviewRemarks || '',
      terminationNoticePeriodValue: terminationValue,
      terminationNoticePeriodUnit: terminationUnit,
      price: contract.price ?? null,
      rateBase: contract.rateBase || '',
      btgServiceCharge: contract.btgServiceCharge ?? null,
      btgServiceFee: contract.btgServiceFee ?? null,
      cpiServiceCharge: contract.cpiServiceCharge ?? null,
      cpiClause: contract.cpiClause || '',
      contractLessThanOneYear: contract.contractLessThanOneYear ?? false,
      contractForOneYear: contract.contractForOneYear ?? false,
      contractGreaterThanOneYear: contract.contractGreaterThanOneYear ?? false
    };

    this.displayContractDetailsDialog = true;
    this.loadPriceReviews(contract.id);
  }

  /** Loads the price review records shown under the contract's commercial fields. */
  private loadPriceReviews(contractId: string): void {
    this.priceReviews = [];
    this.loadingPriceReviews = true;
    this.contractService.getPriceReviews(contractId).subscribe({
      next: (reviews) => {
        this.priceReviews = reviews || [];
        this.loadingPriceReviews = false;
      },
      error: () => {
        this.loadingPriceReviews = false;
        this.messageService.add({
          severity: 'error', summary: 'Error', detail: 'Failed to load price review history'
        });
      }
    });
  }

  /**
   * True when the next renewal falls before the last review — almost always a typo.
   * Advisory only: the save is not blocked, since a back-dated correction is valid.
   */
  isNextRenewalBeforeLastReview(): boolean {
    const last = this.contractDetails.lastPriceReviewed;
    const next = this.contractDetails.nextPriceRenewalDate;
    if (!last || !next) return false;
    return next.getTime() < last.getTime();
  }

  togglePriceReviews(): void {
    this.priceReviewsExpanded = !this.priceReviewsExpanded;
  }

  /** Opens a price review read-only. */
  openPriceReview(review: ContractPriceReview): void {
    this.selectedPriceReview = review;
    this.displayPriceReviewDialog = true;
  }

  hidePriceReviewDialog(): void {
    this.displayPriceReviewDialog = false;
    this.selectedPriceReview = null;
  }

  // ===== Jobs (priced jobs under a contract) =====

  showJobsDialog(contract: ContractMaintenance): void {
    this.jobsContract = { ...contract };
    this.jobs = [];
    this.displayJobsDialog = true;
    this.loadJobs(contract.id!);
  }

  hideJobsDialog(): void {
    // The form dialog stacks on top of this one and reads jobsContract on save, so it must
    // never be left open once the contract behind it is gone.
    this.hideJobForm();
    this.displayJobsDialog = false;
    this.jobsContract = null;
    this.jobs = [];
  }

  private loadJobs(contractId: string): void {
    this.loadingJobs = true;
    this.contractService.getJobs(contractId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (jobs) => {
        this.jobs = this.sortJobsByRenewal(jobs);
        this.loadingJobs = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load jobs' });
        this.loadingJobs = false;
      }
    });
  }

  /**
   * Soonest renegotiation first, because that is what the list is opened to check. Jobs with
   * no renewal date sort last rather than to the top, where they would push out the due ones.
   */
  private sortJobsByRenewal(jobs: ContractJob[]): ContractJob[] {
    return [...jobs].sort((a, b) => {
      const aDate = this.parseToDate(a.nextPriceRenewalDate)?.getTime();
      const bDate = this.parseToDate(b.nextPriceRenewalDate)?.getTime();
      if (aDate == null && bDate == null) return (a.name ?? '').localeCompare(b.name ?? '');
      if (aDate == null) return 1;
      if (bDate == null) return -1;
      return aDate - bDate;
    });
  }

  /**
   * True once a job has entered the 3-month reminder window, so the row shows why someone
   * received a renewal email. A renewal date already in the past still counts: the price was
   * never renegotiated and the job needs attention more, not less.
   */
  isJobRenewalDue(job: ContractJob): boolean {
    const renewal = this.parseToDate(job.nextPriceRenewalDate);
    if (!renewal) return false;

    const windowStart = new Date();
    windowStart.setHours(0, 0, 0, 0);
    windowStart.setMonth(windowStart.getMonth() + 3);

    return renewal.getTime() <= windowStart.getTime();
  }

  /** A renewal date before the review date is almost always a typo — warn, do not block. */
  isJobRenewalBeforeReview(draft: ContractJobDraft): boolean {
    if (!draft.lastPriceReviewed || !draft.nextPriceRenewalDate) return false;
    return draft.nextPriceRenewalDate.getTime() < draft.lastPriceReviewed.getTime();
  }

  /** Opens the form blank to add, or populated from an existing job to edit. */
  openJobForm(job?: ContractJob): void {
    this.jobFormMode = job ? 'edit' : 'add';
    this.jobForm = job
      ? {
          id: job.id,
          name: job.name ?? '',
          price: job.price ?? null,
          lastPriceReviewed: this.parseToDate(job.lastPriceReviewed),
          nextPriceRenewalDate: this.parseToDate(job.nextPriceRenewalDate),
          comments: job.comments ?? ''
        }
      : {
          id: null,
          name: '',
          price: null,
          lastPriceReviewed: null,
          nextPriceRenewalDate: null,
          comments: ''
        };
    this.displayJobFormDialog = true;
  }

  hideJobForm(): void {
    this.displayJobFormDialog = false;
    this.jobForm = null;
    this.savingJob = false;
  }

  /** Saves the one job in the form, then refreshes the list behind it. */
  saveJob(): void {
    const form = this.jobForm;
    const contractId = this.jobsContract?.id;
    if (!form || !contractId) return;

    if (!form.name.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Job name required',
        detail: 'Enter a job name before saving.'
      });
      return;
    }

    const payload = {
      contractId,
      name: form.name.trim(),
      price: form.price,
      lastPriceReviewed: this.toDateString(form.lastPriceReviewed),
      nextPriceRenewalDate: this.toDateString(form.nextPriceRenewalDate),
      comments: form.comments?.trim() || null
    };

    this.savingJob = true;

    const request$ = form.id
      ? this.contractService.updateJob(form.id, { ...payload, id: form.id })
      : this.contractService.addJob(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: form.id ? 'Job updated successfully' : 'Job added successfully'
        });
        this.hideJobForm();
        this.loadJobs(contractId);
      },
      error: () => {
        this.savingJob = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: form.id ? 'Failed to update job' : 'Failed to add job'
        });
      }
    });
  }

  /** Deleting a job also drops its pending price-renewal reminders, so confirm first. */
  confirmDeleteJob(job: ContractJob): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${job.name}"? Its price renewal reminders will stop.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteJob(job)
    });
  }

  private deleteJob(job: ContractJob): void {
    const contractId = this.jobsContract?.id;
    if (!contractId) return;

    this.contractService.deleteJob(job.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Job deleted successfully' });
        this.loadJobs(contractId);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete job' });
      }
    });
  }

  /**
   * Signed, 2dp increment label, e.g. "+12.5%". Returns an em dash when there is no
   * previous review or the previous price was zero (the rule says ignore that case).
   */
  formatIncrement(review: ContractPriceReview): string {
    const pct = review.priceIncrementPercentage;
    if (pct == null) return '—';
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct}%`;
  }

  /** 'up' / 'down' / null — drives the arrow and colour on the increment cell. */
  incrementDirection(review: ContractPriceReview): 'up' | 'down' | null {
    const pct = review.priceIncrementPercentage;
    if (pct == null || pct === 0) return null;
    return pct > 0 ? 'up' : 'down';
  }

  hideContractDetailsDialog(): void {
    this.displayContractDetailsDialog = false;
    this.selectedContractForDetails = null;
    this.priceReviews = [];
    this.selectedPriceReview = null;
    this.displayPriceReviewDialog = false;
    // Reset form
    this.contractDetails = {
      lastPriceReviewed: null,
      nextPriceRenewalDate: null,
      priceReviewRemarks: '',
      terminationNoticePeriodValue: null,
      terminationNoticePeriodUnit: 'Days',
      price: null,
      rateBase: '',
      btgServiceCharge: null,
      btgServiceFee: null,
      cpiServiceCharge: null,
      cpiClause: '',
      contractLessThanOneYear: false,
      contractForOneYear: false,
      contractGreaterThanOneYear: false
    };
  }

  saveContractDetails(): void {
    if (!this.selectedContractForDetails) return;

    const contractId = this.selectedContractForDetails.id;

    // Build termination notice period string
    let terminationNoticePeriod: string | null = null;
    if (this.contractDetails.terminationNoticePeriodValue != null && this.contractDetails.terminationNoticePeriodValue > 0) {
      const unit = this.contractDetails.terminationNoticePeriodUnit.toLowerCase();
      const unitLabel = unit === 'weeks' ? 'Week' : unit === 'months' ? 'Month' : 'Day';
      const plural = this.contractDetails.terminationNoticePeriodValue !== 1 ? 's' : '';
      terminationNoticePeriod = `${this.contractDetails.terminationNoticePeriodValue} ${unitLabel}${plural}`;
    }

    const updatedContract: ContractMaintenance = {
      ...this.selectedContractForDetails,
      lastPriceReviewed: this.toDateString(this.contractDetails.lastPriceReviewed),
      nextPriceRenewalDate: this.toDateString(this.contractDetails.nextPriceRenewalDate),
      priceReviewRemarks: this.contractDetails.priceReviewRemarks || null,
      terminationNoticePeriod: terminationNoticePeriod,
      price: this.contractDetails.price ?? null,
      rateBase: this.contractDetails.rateBase || null,
      btgServiceCharge: this.contractDetails.btgServiceCharge ?? null,
      btgServiceFee: this.contractDetails.btgServiceFee ?? null,
      cpiServiceCharge: this.contractDetails.cpiServiceCharge ?? null,
      cpiClause: this.contractDetails.cpiClause || null,
      contractLessThanOneYear: this.contractDetails.contractLessThanOneYear || null,
      contractForOneYear: this.contractDetails.contractForOneYear || null,
      contractGreaterThanOneYear: this.contractDetails.contractGreaterThanOneYear || null
    };

    this.contractService.update(contractId, updatedContract).subscribe({
      next: (returned) => {
        const idx = this.contracts.findIndex(c => c.id === returned.id);
        if (idx >= 0) this.contracts[idx] = returned;
        this.applyFilters();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Contract details updated successfully'
        });
        this.hideContractDetailsDialog();
      },
      error: (error) => {
        console.error('Error updating contract details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.message || 'Failed to update contract details'
        });
      }
    });
  }

  showContractHistory(contract: ContractMaintenance): void {
    this.historyContract = contract;
    this.contractHistoryEntries = [];
    this.expandedHistoryVersions = {};
    this.displayHistoryDialog = true;
    this.loadingHistory = true;
    this.contractService.getHistory(contract.id).subscribe({
      next: (entries) => {
        // Backend returns newest-first; sort defensively so the view does not
        // depend on server ordering.
        this.contractHistoryEntries = [...(entries || [])].sort((a, b) => {
          const diff = new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime();
          return diff !== 0 ? diff : b.version - a.version;
        });
        this.loadingHistory = false;
      },
      error: () => {
        this.loadingHistory = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load contract history' });
      }
    });
  }

  hideContractHistory(): void {
    this.displayHistoryDialog = false;
    this.historyContract = null;
    this.contractHistoryEntries = [];
    this.expandedHistoryVersions = {};
  }

  /** Expand/collapse the field-level change detail for a history row. */
  toggleHistoryDetail(entry: ContractAuditLogEntry): void {
    this.expandedHistoryVersions[entry.id] = !this.expandedHistoryVersions[entry.id];
  }

  isHistoryDetailExpanded(entry: ContractAuditLogEntry): boolean {
    return !!this.expandedHistoryVersions[entry.id];
  }

  /** Short "what changed" label for the row, e.g. "Price, CPI Clause, Status". */
  getHistoryChangedSummary(entry: ContractAuditLogEntry): string {
    if (this.isCreationEntry(entry)) return 'Initial version';
    const rows = this.getHistoryChangeRows(entry);
    if (!rows.length) return 'No field changes';
    return rows.map(r => r.label).join(', ');
  }

  /**
   * Field-by-field Old -> New rows for a version, derived by diffing this entry's
   * snapshot against the next-older entry. Falls back to the server-supplied
   * ChangedFieldsSummary to decide which fields to show when present.
   */
  getHistoryChangeRows(entry: ContractAuditLogEntry): ContractHistoryChange[] {
    const current = this.getHistorySnapshot(entry);
    if (!current) return [];

    // Creation row: show every populated field as the starting state.
    if (this.isCreationEntry(entry)) {
      return CONTRACT_AUDIT_FIELDS
        .map(f => ({
          key: f.key,
          label: f.label,
          previous: '—',
          current: this.formatSnapshotValue(this.readSnapshot(current, f.key), f.type)
        }))
        .filter(r => r.current !== '—');
    }

    const previousEntry = this.getPreviousHistoryEntry(entry);
    const previous = previousEntry ? this.getHistorySnapshot(previousEntry) : null;

    // Restrict to the fields the server flagged, when it supplied a summary.
    const flagged = entry.changedFieldsSummary
      ? new Set(entry.changedFieldsSummary.split(',').map(s => s.trim()).filter(Boolean))
      : null;

    const rows: ContractHistoryChange[] = [];
    for (const f of CONTRACT_AUDIT_FIELDS) {
      if (flagged && !flagged.has(f.key)) continue;

      const currentValue = this.formatSnapshotValue(this.readSnapshot(current, f.key), f.type);
      const previousValue = previous
        ? this.formatSnapshotValue(this.readSnapshot(previous, f.key), f.type)
        : '—';

      // Without a server summary, fall back to comparing the formatted values.
      if (!flagged && currentValue === previousValue) continue;

      rows.push({ key: f.key, label: f.label, previous: previousValue, current: currentValue });
    }
    return rows;
  }

  private isCreationEntry(entry: ContractAuditLogEntry): boolean {
    return entry.action === 'Created' || entry.version === 1;
  }

  /** The next-older entry for this contract (list is sorted newest-first). */
  private getPreviousHistoryEntry(entry: ContractAuditLogEntry): ContractAuditLogEntry | null {
    const idx = this.contractHistoryEntries.findIndex(e => e.id === entry.id);
    if (idx < 0 || idx + 1 >= this.contractHistoryEntries.length) return null;
    return this.contractHistoryEntries[idx + 1];
  }

  /** Reads a snapshot key tolerating both PascalCase and camelCase serialization. */
  private readSnapshot(s: Record<string, unknown>, key: string): unknown {
    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    const v = s[key] ?? s[camel];
    return v ?? null;
  }

  private formatSnapshotValue(value: unknown, type: ContractAuditFieldType): string {
    if (value === null || value === undefined || value === '') return '—';
    switch (type) {
      case 'date':
        return this.formatSnapshotDate(String(value));
      case 'bool':
        return value === true || value === 'true' ? 'Yes' : 'No';
      case 'number': {
        const n = Number(value);
        return isNaN(n) ? String(value) : n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      default:
        return String(value);
    }
  }

  /** Human-readable summary from audit snapshot JSON (e.g. "Client X, MSA Y, Status Active"). */
  getHistorySnapshotSummary(entry: ContractAuditLogEntry): string {
    if (!entry.snapshotJson) return '—';
    try {
      const s = this.getHistorySnapshot(entry);
      if (!s) return '—';
      const parts: string[] = [];
      const cn = s['clientName'] ?? s['ClientName'];
      const ct = s['contractType'] ?? s['ContractType'];
      const mn = s['msaName'] ?? s['MsaName'];
      const pn = s['projectName'] ?? s['ProjectName'];
      const st = s['status'] ?? s['Status'];
      if (cn) parts.push(String(cn));
      if (ct) parts.push('Type: ' + String(ct));
      if (mn) parts.push('MSA: ' + String(mn));
      if (pn && pn !== 'N/A') parts.push('Project: ' + String(pn));
      if (st) parts.push('Status: ' + String(st));
      return parts.length ? parts.join(' · ') : '—';
    } catch {
      return '—';
    }
  }

  /** Signed date from snapshot (by contract type: MSA/SOW/Schedule/Proposal). */
  getHistorySignedDate(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const ct = (s['contractType'] ?? s['ContractType']) as string;
    const d = this.getSnapshotDateByType(s, ct, 'Signed');
    return d ? this.formatSnapshotDate(d) : '—';
  }

  /** Expiry date from snapshot (by contract type). */
  getHistoryExpiryDate(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const ct = (s['contractType'] ?? s['ContractType']) as string;
    const d = this.getSnapshotDateByType(s, ct, 'Expiry');
    return d ? this.formatSnapshotDate(d) : '—';
  }

  /** Duration from snapshot (by contract type). */
  getHistoryDuration(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const ct = (s['contractType'] ?? s['ContractType']) as string;
    const dur = this.getSnapshotDurationByType(s, ct);
    return dur ?? '—';
  }

  getHistoryServiceGroup(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const v = s['serviceGroupName'] ?? s['ServiceGroupName'];
    return v != null && v !== '' ? String(v) : '—';
  }

  getHistoryCountry(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const v = s['countryName'] ?? s['CountryName'] ?? s['countyName'] ?? s['CountyName'];
    return v != null && v !== '' ? String(v) : '—';
  }

  getHistoryCompany(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const v = s['companyName'] ?? s['CompanyName'];
    return v != null && v !== '' ? String(v) : '—';
  }

  getHistoryStatus(entry: ContractAuditLogEntry): string {
    const s = this.getHistorySnapshot(entry);
    if (!s) return '—';
    const v = s['status'] ?? s['Status'];
    return v != null && v !== '' ? String(v) : '—';
  }

  get totalContractsCount(): number { return this.contracts.length; }
  get activeContractsCount(): number { return this.contracts.filter(c => c.status === 'Active').length; }
  get inactiveContractsCount(): number { return this.contracts.filter(c => c.status !== 'Active').length; }
  get showingCustomersCount(): number { return this.filteredCustomerGroups.length; }

  getStatusSeverity(status: string): 'success' | 'danger' | 'secondary' {
    return status === 'Active' ? 'success' : 'danger';
  }

  getTypeSeverity(type: string): 'info' | 'success' | 'warn' | 'secondary' {
    switch (type) {
      case 'MSA': return 'info';
      case 'SOW': return 'success';
      case 'Proposal': return 'warn';
      case 'Schedule': return 'secondary';
      default: return 'secondary';
    }
  }

  private getHistorySnapshot(entry: ContractAuditLogEntry): Record<string, unknown> | null {
    if (!entry.snapshotJson) return null;
    try {
      return JSON.parse(entry.snapshotJson) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private getSnapshotDateByType(s: Record<string, unknown>, contractType: string, kind: 'Signed' | 'Expiry'): string | null {
    const suf = kind === 'Signed' ? 'SignedDate' : 'ExpiryDate';
    const keys = contractType === 'MSA' ? [`Msa${suf}`, `msa${suf}`]
      : contractType === 'SOW' ? [`Sow${suf}`, `sow${suf}`]
      : contractType === 'Schedule' ? [`Schedule${suf}`, `schedule${suf}`]
      : contractType === 'Proposal' ? [`Proposal${suf}`, `proposal${suf}`]
      : [];
    for (const k of keys) {
      const v = s[k];
      if (v != null && v !== '') return typeof v === 'string' ? v : (v as Date)?.toString?.() ?? null;
    }
    const fallbacks = [`Msa${suf}`, `Sow${suf}`, `Schedule${suf}`, `Proposal${suf}`];
    for (const k of fallbacks) {
      const v = s[k] ?? s[k.charAt(0).toLowerCase() + k.slice(1)];
      if (v != null && v !== '') return typeof v === 'string' ? v : (v as Date)?.toString?.() ?? null;
    }
    return null;
  }

  private getSnapshotDurationByType(s: Record<string, unknown>, contractType: string): string | null {
    const keys = contractType === 'MSA' ? ['MsaDuration', 'msaDuration']
      : contractType === 'SOW' ? ['SowDuration', 'sowDuration']
      : contractType === 'Schedule' ? ['ScheduleDuration', 'scheduleDuration']
      : contractType === 'Proposal' ? ['ProposalDuration', 'proposalDuration']
      : [];
    for (const k of keys) {
      const v = s[k];
      if (v != null && v !== '') return String(v);
    }
    const fallbacks = ['MsaDuration', 'SowDuration', 'ScheduleDuration', 'ProposalDuration'];
    for (const k of fallbacks) {
      const v = s[k] ?? s[k.charAt(0).toLowerCase() + k.slice(1)];
      if (v != null && v !== '') return String(v);
    }
    return null;
  }

  private formatSnapshotDate(d: string): string {
    if (!d) return '—';

    // If it's already a plain date string (YYYY-MM-DD), use it as-is
    const plainDateMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (plainDateMatch) {
      return `${plainDateMatch[1]}-${plainDateMatch[2]}-${plainDateMatch[3]}`;
    }

    // Otherwise parse and format using local date parts (avoid UTC shift from toISOString)
    try {
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch {
      return d;
    }
  }

  exportToExcel(): void {
    if (!this.filteredContracts || this.filteredContracts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No contracts to export'
      });
      return;
    }

    try {
      // Prepare data for Excel export - flatten customer groups
      const exportData: any[] = [];

      for (const customerGroup of this.filteredCustomerGroups) {
        for (const contract of customerGroup.contracts) {
          exportData.push({
            'Customer Name': contract.clientName || '',
            'Folder Number': contract.folderNumber || '',
            'Contract Type': contract.contractType || '',
            'MSA Name': contract.msaName || '',
            'Project Name': contract.projectName || 'N/A',
            'Service Group': contract.serviceGroupName || '',
            'Country': contract.countryName || '',
            'Company': contract.companyName || '',
            'Status': contract.status || '',
            'Effective Date': contract.effectiveDate ? new Date(contract.effectiveDate).toLocaleDateString() : '',
            'MSA Signed Date': contract.msaSignedDate ? new Date(contract.msaSignedDate).toLocaleDateString() : '',
            'MSA Duration': contract.msaDuration || '',
            'MSA Expiry Date': contract.msaExpiryDate ? new Date(contract.msaExpiryDate).toLocaleDateString() : '',
            'SOW Signed Date': contract.sowSignedDate ? new Date(contract.sowSignedDate).toLocaleDateString() : '',
            'SOW Duration': contract.sowDuration || '',
            'SOW Expiry Date': contract.sowExpiryDate ? new Date(contract.sowExpiryDate).toLocaleDateString() : '',
            'Schedule Signed Date': contract.scheduleSignedDate ? new Date(contract.scheduleSignedDate).toLocaleDateString() : '',
            'Schedule Duration': contract.scheduleDuration || '',
            'Schedule Expiry Date': contract.scheduleExpiryDate ? new Date(contract.scheduleExpiryDate).toLocaleDateString() : '',
            'Proposal Signed Date': contract.proposalSignedDate ? new Date(contract.proposalSignedDate).toLocaleDateString() : '',
            'Proposal Duration': contract.proposalDuration || '',
            'Proposal Expiry Date': contract.proposalExpiryDate ? new Date(contract.proposalExpiryDate).toLocaleDateString() : '',
            'Last Price Reviewed': contract.lastPriceReviewed ? new Date(contract.lastPriceReviewed).toLocaleDateString() : '',
            'Next Price Renewal Date': contract.nextPriceRenewalDate ? new Date(contract.nextPriceRenewalDate).toLocaleDateString() : '',
            'Price Review Remarks': contract.priceReviewRemarks || '',
            'Termination Notice Period': contract.terminationNoticePeriod || '',
            'Price': contract.price ?? '',
            'Rate Base': contract.rateBase || '',
            'BTG Service Charge': contract.btgServiceCharge ?? '',
            'BTG Service Fee': contract.btgServiceFee ?? '',
            'CPI Service Charge': contract.cpiServiceCharge ?? '',
            'CPI Clause': contract.cpiClause || '',
            'Contract < 1 Year': contract.contractLessThanOneYear ? 'Yes' : 'No',
            'Contract for 1 Year': contract.contractForOneYear ? 'Yes' : 'No',
            'Contract > 1 Year': contract.contractGreaterThanOneYear ? 'Yes' : 'No'
          });
        }
      }

      // Create worksheet
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 20 }, // Customer Name
        { wch: 15 }, // Folder Number
        { wch: 14 }, // Contract Type
        { wch: 22 }, // MSA Name
        { wch: 25 }, // Project Name
        { wch: 20 }, // Service Group
        { wch: 15 }, // Country
        { wch: 20 }, // Company
        { wch: 12 }, // Status
        { wch: 15 }, // MSA Signed Date
        { wch: 15 }, // MSA Duration
        { wch: 15 }, // MSA Expiry Date
        { wch: 15 }, // SOW Signed Date
        { wch: 15 }, // SOW Duration
        { wch: 15 }, // SOW Expiry Date
        { wch: 18 }, // Schedule Signed Date
        { wch: 15 }, // Schedule Duration
        { wch: 18 }, // Schedule Expiry Date
        { wch: 18 }, // Proposal Signed Date
        { wch: 15 }, // Proposal Duration
        { wch: 18 }, // Proposal Expiry Date
        { wch: 18 }, // Last Price Reviewed
        { wch: 30 }, // Price Review Remarks
        { wch: 22 }, // Termination Notice Period
        { wch: 12 }, // Price
        { wch: 15 }, // Rate Base
        { wch: 18 }, // BTG Service Charge
        { wch: 15 }, // BTG Service Fee
        { wch: 18 }, // CPI Service Charge
        { wch: 30 }, // CPI Clause
        { wch: 18 }, // Contract < 1 Year
        { wch: 18 }, // Contract for 1 Year
        { wch: 18 }  // Contract > 1 Year
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Contracts': worksheet },
        SheetNames: ['Contracts']
      };

      // Generate Excel file
      const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      // Create blob and save file
      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fileName = `Contracts_Maintenance_${this.formatDateForExcel(new Date())}.xlsx`;
      saveAs(data, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Contracts exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export contracts to Excel'
      });
    }
  }

  private formatDateForExcel(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }
}